import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { DepositTypeList } from '@/features/add-money/select-deposit-type'
import { useAddMoneyStore, PaymentMethodType } from '@/store/useAddMoneyStore'
import { StickyHeaderNavigation } from '@/components'
import { SHOW_APPLE_PAY } from '@/config/app'

export const SelectDepositTypeScreen: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const { selectedCurrency, setSelectedCurrency } = useAddMoneyStore()

  const handleBackPress = () => {
    navigate('/dashboard')
  }

  // Get currency data from location.state (if provided)
  const currencyFromState = location.state

  // Get params from URL (similar to RN version)
  const isBuy = searchParams.get('isBuy') === '1'
  const currencyCode =
    currencyFromState?.code || searchParams.get('code') || 'USDT'

  // Create defaultCurrency based on provided data or stub
  const defaultCurrency = React.useMemo(() => {
    // Determine available payment methods based on SHOW_APPLE_PAY
    const getDefaultPaymentMethods = (): PaymentMethodType[] => {
      const methods: PaymentMethodType[] = ['card', 'bank-transfer']
      if (SHOW_APPLE_PAY) {
        methods.unshift('apple-pay')
      }
      return methods
    }

    if (currencyFromState) {
      return {
        id: currencyFromState.id || '1',
        name: currencyFromState.name || currencyFromState.code || 'Unknown',
        code: currencyFromState.code || 'USDT',
        amount: currencyFromState.amount || 0,
        amountWithoutNumberConversion: String(currencyFromState.amount || 0),
        icon:
          currencyFromState.icon ||
          'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
        label: currencyFromState.label || '$',
        fiatAmount: currencyFromState.fiatAmount || 0,
        fiatCurrency: currencyFromState.fiatCurrency || 'USD',
        pricePerUnit: currencyFromState.pricePerUnit || 1,
        ceilingValueForValidation:
          currencyFromState.ceilingValueForValidation || 0,
        availableOnRampPaymentMethods:
          currencyFromState.availableOnRampPaymentMethods ||
          getDefaultPaymentMethods(),
        formattedFiatBalance: currencyFromState.formattedFiatBalance || '$0.00',
        formattedAmountBalance: currencyFromState.formattedAmountBalance || '0',
        formattedUSDBalance: currencyFromState.formattedUSDBalance || '$0.00',
        formattedStablecoinBalance:
          currencyFromState.formattedStablecoinBalance || '0',
        usdBalance: currencyFromState.usdBalance || 0,
        displayCurrencyCode:
          currencyFromState.displayCurrencyCode ||
          currencyFromState.code ||
          'USDT',
        symbol: currencyFromState.symbol || '$',
        avatarBGRefColor: currencyFromState.avatarBGRefColor || '#33CC67',
      }
    } else {
      // Fallback to stub (as before)
      return {
        id: '1',
        name: currencyCode === 'USDT' ? 'Tether' : 'Unknown',
        code: currencyCode,
        amount: 0,
        amountWithoutNumberConversion: '0',
        icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
        label: '$',
        fiatAmount: 0,
        fiatCurrency: 'USD',
        pricePerUnit: 1,
        ceilingValueForValidation: 0,
        availableOnRampPaymentMethods: getDefaultPaymentMethods(),
        formattedFiatBalance: '$0.00',
        formattedAmountBalance: '0',
        formattedUSDBalance: '$0.00',
        formattedStablecoinBalance: '0',
        usdBalance: 0,
        displayCurrencyCode: currencyCode,
        symbol: '$',
        avatarBGRefColor: '#33CC67',
      }
    }
  }, [currencyFromState, currencyCode])

  // Set selected currency on load
  // Priority: currencyFromState > selectedCurrency
  useEffect(() => {
    // If there is a currency from state, always set it
    if (currencyFromState) {
      setSelectedCurrency(currencyFromState)
    } else if (defaultCurrency && !selectedCurrency) {
      // Otherwise if no selectedCurrency, set defaultCurrency
      setSelectedCurrency(defaultCurrency)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyFromState?.code]) // Track only currency code changes from state

  // Use current currency: priority currencyFromState > selectedCurrency > defaultCurrency
  const actualCurrency =
    currencyFromState || selectedCurrency || defaultCurrency

  return (
    <BaseSafeFormView
      style={{ paddingTop: 50 }}
      flex={1}
      bgColor="background.screen"
      className="h-full"
    >
      {/* Header Navigation */}
      <StickyHeaderNavigation onBackPress={handleBackPress} />
      {/* <BaseView paddingTop={offsets.top} px={13} flex={1} bgColor="background.screen"> */}

      {/* Content */}
      <BaseView mt={16} px={13} flex={1} gap={20}>
        {/* Title */}
        <BaseText fontSize={28} fontFamily="semibold" color="label.default">
          {t('screens:selectDepositType.title')}
        </BaseText>

        {/* Deposit Types List */}
        <DepositTypeList isBuy={isBuy} walletData={actualCurrency} />
      </BaseView>
    </BaseSafeFormView>
  )
}
